import { useRouterState } from "@tanstack/react-router";
import { useEffect, useState } from "react";

/** Below this, a navigation is quick enough that a bar would only flicker. */
const SHOW_AFTER_MS = 120;

type Phase = "idle" | "start" | "run" | "done";

/**
 * Thin top-of-page progress bar while the router is navigating. With route code split
 * into chunks, a click changes the URL and then waits for that page's chunk; on a slow
 * network or a busy server that looked like nothing was happening (Nick 10-15, staging).
 * The bar creeps towards 85% while pending, then fills and fades when the page renders.
 */
export function RouteProgress() {
  const busy = useRouterState({ select: (s) => s.status === "pending" || s.isLoading });
  const [phase, setPhase] = useState<Phase>("idle");

  useEffect(() => {
    if (busy) {
      let frame = 0;
      const timer = window.setTimeout(() => {
        setPhase("start");
        // Two frames so the 0% width paints before the transition to 85% starts.
        frame = requestAnimationFrame(() => {
          frame = requestAnimationFrame(() => setPhase("run"));
        });
      }, SHOW_AFTER_MS);
      return () => {
        window.clearTimeout(timer);
        cancelAnimationFrame(frame);
      };
    }
    setPhase((p) => (p === "idle" ? "idle" : "done"));
    const timer = window.setTimeout(() => setPhase("idle"), 450);
    return () => window.clearTimeout(timer);
  }, [busy]);

  if (phase === "idle") return null;
  const width = phase === "start" ? "0%" : phase === "run" ? "85%" : "100%";
  const transition =
    phase === "run"
      ? "width 10s cubic-bezier(0.1, 0.7, 0.2, 1)"
      : phase === "done"
        ? "width 200ms ease-out, opacity 250ms ease 200ms"
        : "none";
  return (
    <div aria-hidden className="pointer-events-none fixed inset-x-0 top-0 z-[100] h-[3px]">
      <div
        className="h-full bg-primary shadow-[0_0_8px_var(--color-primary)]"
        style={{ width, transition, opacity: phase === "done" ? 0 : 1 }}
      />
    </div>
  );
}
